import React from "react";
import { Money } from "@/helpers/Money";
import { MarketingIcon } from "@/components/icons";
import { variantSelect } from "@/store";

// Types for clarity
export type PromoEntry = {
  collection: string;
  discountType: string;
  discountValue: number;
  discountCode: string;
};

export type Collection = { id: string; handle: string; title: string };

export function getFinalAtcCtaPrice({
  productCollections,
  basePriceCents,
  sellingPlan,
  variant,
  product,
  currency = "$",
  strikeThroughSize,
  priceSize,
  strikeThroughColor,
  priceColor,
  bannerPadding,
}: {
  productCollections: Collection[] | undefined;
  basePriceCents: number | undefined;
  sellingPlan: any;
  variant: any;
  product?: any;
  currency?: "$" | "¢";
  strikeThroughSize?: string;
  priceSize?: string;
  strikeThroughColor?: string;
  priceColor?: string;
  bannerPadding?: string;
}): {
  display: React.ReactNode;
  discounted: string | null;
  banner: React.ReactNode | null;
  currentPrice: React.ReactNode;
} {
  const promoEntries: PromoEntry[] = (window as any).promoDiscountEntries || [];

  // Find the best matching promo
  const matchingPromo = promoEntries
    .filter((entry) =>
      productCollections?.some(
        (col) => col.handle === entry.collection || col.id === entry.collection
      )
    )
    .sort((a, b) => b.discountValue - a.discountValue)[0];

  const hasTieredMultiplierFeature =
    variantSelect.hasTieredMultiplierFeature(product);
  const hasVariantQuantityDiscount =
    variantSelect.quantityDiscountPercent(variant) > 0;
  const defaultVariantMultiplier = Number(
    variant?.default_variant_multiplier ??
      variant?.metafields?.default_variant_multiplier ??
      1
  );
  const hasDefaultMultiplier =
    Number.isFinite(defaultVariantMultiplier) && defaultVariantMultiplier > 1;
  const tieredMultiplier = Number(
    variant?.tiered_discount_variant_multiplier ??
      variant?.metafields?.tiered_discount_variant_multiplier ??
      1
  );
  const hasTieredMultiplier =
    Number.isFinite(tieredMultiplier) && tieredMultiplier > 1;
  const isPromoDiscountEligible =
    !hasVariantQuantityDiscount &&
    !hasDefaultMultiplier &&
    !hasTieredMultiplier &&
    (matchingPromo || false);
  const subscriptionPrice = variantSelect.lowestPrice(variant, product);
  const onetimePrice = variantSelect.onetimePrice(variant, product);

  // If test is off or no promo, return the normal price
  if (!isPromoDiscountEligible) {
    return {
      display: sellingPlan ? subscriptionPrice.format() : onetimePrice.format(),
      discounted: null,
      banner: null,
      currentPrice: sellingPlan
        ? subscriptionPrice.format()
        : onetimePrice.format(),
    };
  }

  // Calculate discounted price
  const {
    discountType,
    discountValue,
    discountCode = "",
  } = isPromoDiscountEligible;
  const type = discountType?.toLowerCase();

  let finalPrice = basePriceCents ?? 0;

  if (type === "percentage") {
    finalPrice = Math.ceil(finalPrice * (1 - (discountValue ?? 0) / 100));
  } else if (type === "fixed") {
    finalPrice = Math.max(
      0,
      finalPrice - Math.ceil((discountValue ?? 0) * 100)
    );
  }

  const formattedDiscounted = Money.from(finalPrice / 100, currency).format();
  const originalPrice = sellingPlan
    ? subscriptionPrice.format()
    : onetimePrice.format();

  // Banner JSX if eligible
  let banner: React.ReactNode = null;
  if (discountValue && discountCode) {
    const discountText =
      type === "fixed" ? `$${discountValue} OFF` : `${discountValue}% OFF`;

    banner = (
      <div
        className={`mt-4 flex items-center justify-center rounded-[3px] border border-solid border-secondary-500 bg-secondary-100 ${
          bannerPadding || "px-4 py-[10px]"
        }`}
      >
        <MarketingIcon />
        <p className="ml-[10px] text-left text-xs">
          You`re getting <strong>{discountText}</strong> with code{" "}
          <strong>{discountCode}</strong> auto-applied
        </p>
      </div>
    );
  }

  // Return display as React.createElement (for .ts file compatibility)
  return {
    display: (
      <>
        <span
          className={`mr-2 font-normal line-through ${strikeThroughSize} ${strikeThroughColor} group-hover:text-white`}
        >
          {originalPrice}
        </span>
        <span className={`${priceSize} ${priceColor} group-hover:text-white`}>
          {formattedDiscounted}
        </span>
      </>
    ),
    discounted: formattedDiscounted,
    banner,
    currentPrice: originalPrice,
  };
}
